import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { profileKeys } from "@/application/query-keys/profile.keys";
import { profileRepository } from "@/infrastructure/repositories/profile.supabase.repository";
import type { ProfileRow } from "@/infrastructure/supabase/database.types";

/**
 * The signed-in user's access-control profile. Gated on `userId` (from the
 * established session) so it never fires before the session is known — that
 * premature fetch used to cache a null profile and leave the user fail-closed
 * until a manual refresh. When `userId` arrives (or changes) the query enables
 * and fetches the right row.
 */
export function useCurrentProfile(userId: string | undefined) {
  return useQuery({
    queryKey: profileKeys.current(userId),
    // `enabled` only gates automatic fetches — a manual refetch() could still run
    // this with no user, so guard explicitly rather than casting `userId as string`.
    queryFn: () => {
      if (!userId) throw new Error("useCurrentProfile requires a signed-in user id");
      return profileRepository.current(userId);
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

/** All users, for the admin Users page (BF-215). RLS returns all rows only to an admin. */
export function useAllProfiles(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: profileKeys.list(),
    queryFn: () => profileRepository.listAll(),
    enabled: options?.enabled ?? true,
    staleTime: 60 * 1000,
  });
}

/** Update a user's access (admin-only, RLS-gated). Optimistically patches the list. */
export function useUpdateProfileAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<Pick<ProfileRow, "workspaces" | "can_write" | "is_admin" | "role">>;
    }) => profileRepository.updateAccess(id, patch),
    onMutate: async ({ id, patch }) => {
      await queryClient.cancelQueries({ queryKey: profileKeys.list() });
      const prev = queryClient.getQueryData<ProfileRow[]>(profileKeys.list());
      queryClient.setQueryData<ProfileRow[]>(profileKeys.list(), (old) =>
        old ? old.map((p) => (p.id === id ? { ...p, ...patch } : p)) : old
      );
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(profileKeys.list(), ctx.prev);
      console.error("[useUpdateProfileAccess]", err);
    },
    onSuccess: (row) => {
      queryClient.setQueryData<ProfileRow[]>(profileKeys.list(), (old) =>
        old ? old.map((p) => (p.id === row.id ? row : p)) : old
      );
    },
  });
}
